// bt-common/engine/engine-descriptor.ts
// #922 S0a: one descriptor per EngineKind = "what this engine is". A mapped record, so adding a kind
// without a descriptor fails tsc. Values for the three WA-Web kinds restate what the code hard-codes today.
import { BAILEYS_SUPPORTED_COMMANDS, CHROME_SUPPORTED_COMMANDS, type WaToolCommand } from '../interfaces/whatsapp-tool-contracts.interface';
import type { SendFailEngine } from './send-failure';
import { ENGINE_KINDS, type EngineFamily, type EngineKind, type GatewayType, type LogClientType, type SentWith, type SessionEngineType } from './engine-kind';
import { CLOUD_API_POLICY, WA_WEB_POLICY, type EnginePolicy } from './policy';

/** Ids this kind WRITES into legacy persisted fields. Write derivation only: NOT invertible
 *  (`sentWith:'gateway'` is shared by two kinds). Reads go through the classifiers. */
export interface LegacyEngineIds {
    clientType: LogClientType;
    sentWith: SentWith;
    failEngine: SendFailEngine;
    engineType: SessionEngineType | null;
    gatewayType: GatewayType;
}

export type EngineLifecycle = 'heartbeat' | 'pod' | 'server-managed';
export type EngineEntitlement = 'none' | 'offline-socket' | 'cloud-api';

export interface EngineDescriptor<K extends EngineKind = EngineKind> {
    kind: K;
    family: EngineFamily;
    commands: readonly WaToolCommand[];
    transport: 'mqtt' | 'in-process';
    lifecycle: EngineLifecycle;
    entitlement: EngineEntitlement;
    alwaysOn: boolean;
    reconcilers: { pendingAck: boolean; storeProbe: boolean };
    policy: EnginePolicy;
    /** null = the kind has no legacy persisted ids (cloud-api: nothing existing may produce or store it). */
    legacy: LegacyEngineIds | null;
}

const chromeCommands: readonly WaToolCommand[] = Object.keys(CHROME_SUPPORTED_COMMANDS).filter(isWaToolCommand);
const baileysCommands: readonly WaToolCommand[] = Object.keys(BAILEYS_SUPPORTED_COMMANDS).filter(isWaToolCommand);

function isWaToolCommand(k: string): k is WaToolCommand {
    return k in CHROME_SUPPORTED_COMMANDS || k in BAILEYS_SUPPORTED_COMMANDS;
}

export const ENGINE_DESCRIPTORS = {
    extension: {
        kind: 'extension', family: 'wa-web', commands: chromeCommands, transport: 'mqtt', lifecycle: 'heartbeat',
        entitlement: 'none', alwaysOn: false, reconcilers: { pendingAck: true, storeProbe: true }, policy: WA_WEB_POLICY,
        legacy: { clientType: 'whatsapp', sentWith: 'whatsapp-web', failEngine: 'wa-web-native', engineType: null, gatewayType: '' },
    },
    'chrome-gateway': {
        kind: 'chrome-gateway', family: 'wa-web', commands: chromeCommands, transport: 'mqtt', lifecycle: 'pod',
        entitlement: 'offline-socket', alwaysOn: true, reconcilers: { pendingAck: true, storeProbe: true }, policy: WA_WEB_POLICY,
        legacy: { clientType: 'gateway', sentWith: 'gateway', failEngine: 'wa-web-native', engineType: 'session-pod', gatewayType: 'wa-js-gcp' },
    },
    baileys: {
        kind: 'baileys', family: 'wa-web', commands: baileysCommands, transport: 'mqtt', lifecycle: 'pod',
        entitlement: 'offline-socket', alwaysOn: true, reconcilers: { pendingAck: true, storeProbe: true }, policy: WA_WEB_POLICY,
        legacy: { clientType: 'baileys-pod', sentWith: 'gateway', failEngine: 'baileys-pod', engineType: 'baileys-pod', gatewayType: 'baileys' },
    },
    'cloud-api': {
        kind: 'cloud-api', family: 'cloud', commands: [], transport: 'in-process', lifecycle: 'server-managed',
        entitlement: 'cloud-api', alwaysOn: true, reconcilers: { pendingAck: false, storeProbe: false }, policy: CLOUD_API_POLICY,
        legacy: null,
    },
} as const satisfies { [K in EngineKind]: EngineDescriptor<K> };

export function descriptorOf<K extends EngineKind>(kind: K): (typeof ENGINE_DESCRIPTORS)[K] {
    return ENGINE_DESCRIPTORS[kind];
}

/** `sessions.engineType` values of every kind whose session owns a PVC (lifecycle 'pod'), in ENGINE_KINDS order. */
export function podBackedEngineTypes(): readonly SessionEngineType[] {
    const out: SessionEngineType[] = [];
    for (const k of ENGINE_KINDS) {
        const d: EngineDescriptor = ENGINE_DESCRIPTORS[k];
        if (d.lifecycle === 'pod' && d.legacy?.engineType) out.push(d.legacy.engineType);
    }
    return out;
}
